/**
 * Building the `demographic` payload a translation stores.
 *
 * The shape is `build_demo_grid`'s own contract, so nothing converts on the way
 * in or out: each dimension is an object of `level -> weight` summing to 1,
 * which is `parse_dim`'s named-numeric branch.
 *
 * `build_demo_grid` also accepts the string `"typical"` — the mean of a
 * factor's coefficients on the link scale, one synthetic cell. The picker does
 * not offer it. It answers a different question from the weighted levels beside
 * it (link scale versus marginalising probabilities after the link), the two
 * part at the tails where the presence gate sits, and picking between them is
 * not a choice an audience definition should be asking. A named population is
 * what a translation is for. R keeps the branch, so a stored row may still
 * carry it — see `describeDemographic`.
 */

export const DEMO_DIMENSIONS = [
  'country',
  'age_group',
  'gender',
  'smoker',
] as const;

export type DemoDimension = (typeof DEMO_DIMENSIONS)[number];

/** What one dimension stores: a weighted distribution over its levels. */
export type DimensionValue = Record<string, number>;

export type Demographic = Record<DemoDimension, DimensionValue>;

/** Raw, relative weights per selected level — normalised on the way out. */
export type DimensionDraft = Record<string, number>;

export const DIMENSION_LABEL: Record<DemoDimension, string> = {
  country: 'Country',
  age_group: 'Age',
  gender: 'Gender',
  smoker: 'Smoking',
};

/**
 * The factor levels, fixed rather than observed.
 *
 * Mirrors `DEMO_LEVELS` in gastrographpackage, which is the source of truth:
 * `clean_review_data()` decides three of these four outright — `gender` and
 * `smoker` are `fcase()` branches and `age_group` is `cut()` over `AGE_BREAKS`
 * — so nothing else can come out of them.
 *
 * `country` is not like that. It is free text off the user table, so this is
 * the *supported* set (those carrying more than ten panellists) and not a
 * closed vocabulary. A country outside it is refused; one inside it is known
 * to be spellable, not to have been seen by any given model.
 *
 * That distinction is why this is only a fallback. A job that stamped its own
 * `demo_levels` records what it actually trained on, which is the stronger
 * claim and wins — see `effectiveLevels`.
 */
/**
 * The countries the model can be asked about: the value it was fitted on, and
 * the spelling people read.
 *
 * `sql/fetch_reviews.sql` selects `LOWER(u.country)`, so the fitted factor
 * levels are lowercase — `usa`, never `USA`. Sending the wrong case is not a
 * cosmetic mismatch; `assert_trained_levels` refuses the render outright:
 *
 *     Error: demographic levels absent from the fitted data: country [USA]
 *
 * The two spellings live in one table so the value written and the label shown
 * cannot drift apart. Only `country` needs this — `gender`, `age_group` and
 * `smoker` are built by `clean_review_data` in the case they are stored in.
 */
const COUNTRIES: string[] =
  'argentina,australia,brazil,canada,chile,china,colombia,denmark,egypt,france,germany,india,indonesia,italy,japan,korea,mexico,netherlands,nigeria,norway,philippines,poland,portugal,romania,russia,saudi arabia,singapore,south africa,spain,taiwan,thailand,turkey,uk,united arab emirates,usa,vietnam'.split(
    ',',
  );

/** The two the title-case rule would render as "Uk" and "Usa". */
const ACRONYMS: Record<string, string> = { uk: 'UK', usa: 'USA' };

const COUNTRY_LABEL: Record<string, string> = Object.fromEntries(
  COUNTRIES.map((value) => [
    value,
    ACRONYMS[value] ?? value.replace(/\b\w/g, (c) => c.toUpperCase()),
  ]),
);

/**
 * A level as it should be read, given a level as it is stored.
 *
 * Country is the only dimension where the two differ, and they differ because
 * the model lowercased it. Showing `usa` would be honest and unreadable; the
 * value sent is unaffected either way.
 */
export const levelLabel = (level: string): string =>
  COUNTRY_LABEL[level] ?? level;

export const DEMO_LEVELS: Record<DemoDimension, string[]> = {
  // The non-response level keeps its capital: `clean_review_data` writes
  // "Unknown" *after* the SQL has lowercased everything else, so that really is
  // its spelling in the fitted data.
  country: [...COUNTRIES, 'Unknown'],
  age_group: [
    '6-12',
    '13-17',
    '18-20',
    '21-24',
    '25-34',
    '35-44',
    '45-54',
    '55+',
    'Unknown',
  ],
  gender: ['Female', 'Male', 'Unknown'],
  smoker: ['No', 'Yes', 'Unknown'],
};

/** The non-response level, as review cleaning spells it. */
export const NON_RESPONSE = 'Unknown';

/**
 * Dimensions where non-response is not offered as a target.
 *
 * "People whose country we did not record" is not an audience anyone is
 * translating for — it is a gap in the data wearing a level's clothes. Age
 * keeps it: an unparsed age is still a person of some age, and the band is
 * missing rather than the answer.
 *
 * Only the picker is narrowed. `DEMO_LEVELS` still mirrors R, and R's own
 * vocabulary keeps "Unknown" in every dimension, because the model really does
 * carry a coefficient for it — a job that trained on it can render it, and
 * making R refuse that would be wrong.
 */
const TARGETABLE_NON_RESPONSE: Record<DemoDimension, boolean> = {
  country: false,
  age_group: true,
  gender: false,
  smoker: false,
};

/**
 * The levels a picker may offer, per dimension.
 *
 * A job's own `demo_levels` when it stamped them, the fixed vocabulary when it
 * did not, minus non-response where that is not a target. One function so the
 * options on offer and the check applied on save cannot disagree — offering a
 * level the validator then refuses would be worse than either rule alone.
 */
export const effectiveLevels = (
  trainedLevels: Partial<Record<DemoDimension, string[]>> | null,
): Record<DemoDimension, string[]> =>
  DEMO_DIMENSIONS.reduce(
    (acc, d) => {
      const base = trainedLevels?.[d]?.length
        ? trainedLevels[d]
        : DEMO_LEVELS[d];
      return {
        ...acc,
        [d]: TARGETABLE_NON_RESPONSE[d]
          ? base
          : base.filter((l) => l !== NON_RESPONSE),
      };
    },
    {} as Record<DemoDimension, string[]>,
  );

/** True when no stamped levels were available and the vocabulary was used. */
export const isFallbackLevels = (
  trainedLevels: Partial<Record<DemoDimension, string[]>> | null,
): boolean => !DEMO_DIMENSIONS.some((d) => trainedLevels?.[d]?.length);

/** Weights are percentages, so a dimension's levels have to add up to this. */
export const PERCENT_TOTAL = 100;

/** How far a hand-typed total may sit from 100 before it counts as wrong. */
const PERCENT_TOLERANCE = 0.5;

/** What a dimension's percentages currently add up to. */
export const percentTotal = (weights: DimensionDraft): number =>
  Object.values(weights ?? {}).reduce(
    (sum, w) => sum + (Number.isFinite(w) ? w : 0),
    0,
  );

/**
 * An even split across levels, in whole percentage points adding to exactly 100.
 *
 * Thirds do not divide evenly, so the remainder goes to the first levels rather
 * than leaving a total of 99 — a split that does not add up is not a population,
 * and the picker should never open on one.
 */
export const evenPercentages = (levels: string[]): DimensionDraft => {
  if (!levels.length) return {};
  const base = Math.floor(PERCENT_TOTAL / levels.length);
  const remainder = PERCENT_TOTAL - base * levels.length;
  return Object.fromEntries(
    levels.map((l, i) => [l, base + (i < remainder ? 1 : 0)]),
  );
};

/**
 * What the picker opens on, per dimension.
 *
 * A working adult population rather than an empty form: drinking-age adults
 * through 44, both genders, both smoking answers, in one country. Every
 * dimension is split evenly, which is what an unweighted list already means,
 * and every percentage is editable — these are a starting point, not a claim
 * about anyone's panel.
 *
 * "Unknown" is deliberately not in any of them, and `effectiveLevels` does not
 * offer it at all for country, gender or smoking.
 */
export const DEFAULT_LEVELS: Record<DemoDimension, string[]> = {
  country: ['usa'],
  age_group: ['18-20', '21-24', '25-34', '35-44'],
  gender: ['Female', 'Male'],
  smoker: ['No', 'Yes'],
};

/**
 * Population share of each age band.
 *
 * The one dimension where an even split is not neutral: the bands are
 * different widths. "18-20" is three years and "25-34" is ten, so splitting
 * them evenly claims three times too many nineteen-year-olds — a statement
 * about the population, made by a control that looks like it is declining to
 * make one. Gender and smoking have no such problem: their levels are not
 * intervals, so even really is even, and both stay 50/50.
 *
 * Source: `data/demography_data.rda` in gastrographpackage, US rows. That
 * dataset is five-year bins, so each band is the sum of the bins it covers,
 * partial bins taken by fractional overlap — the same mapping
 * `inst/utils/demographic_strata_check.R` uses. Shares of the whole
 * population, so any subset has to be renormalised; `proportionalPercentages`
 * is what does that.
 *
 * ponytail: not re-blended per country. The package carries all 227, and the
 * old console copy blended them behind a "Match population" button that has
 * since been removed as a control nobody used. Band *width* is the dominant
 * term here and is country-independent; if a UK-heavy audience ever needs its
 * own pyramid, this is the table to make country-keyed.
 */
export const AGE_SHARES: Record<string, number> = {
  '6-12': 0.091484,
  '13-17': 0.06702,
  '18-20': 0.041157,
  '21-24': 0.05613,
  '25-34': 0.150131,
  '35-44': 0.139315,
  '45-54': 0.128729,
  '55+': 0.326034,
};

/**
 * Whole percentages over `levels`, in proportion to `shares`.
 *
 * `shares` covers every level of the dimension, so it is renormalised over the
 * ones actually selected — the 18-44 default is a slice of the pyramid, and it
 * has to read as 100% of the audience rather than 39% of a country.
 *
 * Falls back to an even split where the shares say nothing, which is what
 * "Unknown" and any level outside the table amount to.
 */
export const proportionalPercentages = (
  levels: string[],
  shares: Record<string, number>,
): DimensionDraft => {
  if (!levels.length) return {};

  const raw = levels.map((l) => Math.max(0, shares?.[l] ?? 0));
  const total = raw.reduce((a, b) => a + b, 0);
  if (total <= 0) return evenPercentages(levels);

  const scaled = raw.map((v) => (v / total) * PERCENT_TOTAL);
  const rounded = roundToTotal(scaled, PERCENT_TOTAL);
  return Object.fromEntries(levels.map((l, i) => [l, rounded[i]]));
};

/**
 * How one dimension's levels are weighted when the selection changes.
 *
 * Age by population, everything else evenly — see `AGE_SHARES` for why those
 * are different questions rather than an inconsistency.
 */
export const weightLevels = (
  dimension: DemoDimension,
  levels: string[],
): DimensionDraft =>
  dimension === 'age_group'
    ? proportionalPercentages(levels, AGE_SHARES)
    : evenPercentages(levels);

/**
 * The opening draft, narrowed to levels this model actually offers.
 *
 * A job that stamped its own `demo_levels` may not carry every default — a
 * model trained without US reviews has no "usa" — and opening on a level the
 * validator then refuses would be a form that is broken before it is touched.
 * Anything left with no default falls back to every level on offer, since a
 * dimension can no longer be left unspecified.
 */
export const defaultDraft = (
  available: Record<DemoDimension, string[]>,
): Record<DemoDimension, DimensionDraft> => {
  const levelsFor = (d: DemoDimension) => {
    const offered = available?.[d] ?? [];
    const wanted = DEFAULT_LEVELS[d].filter((l) => offered.includes(l));
    return wanted.length ? wanted : offered;
  };

  return DEMO_DIMENSIONS.reduce(
    (acc, d) => ({ ...acc, [d]: weightLevels(d, levelsFor(d)) }),
    {} as Record<DemoDimension, DimensionDraft>,
  );
};

/**
 * Normalise one dimension's weights to sum to 1.
 *
 * Relative numbers in, a distribution out: 70/30 and 7/3 both become
 * 0.7/0.3. R normalises again on the way through `parse_dim`, but the stored
 * row should read as the population it describes rather than as whatever
 * numbers happened to be typed.
 *
 * Returns null when the weights cannot describe a population — no levels, a
 * negative, or a total of zero, which would divide to NaN and only surface
 * from R as "weights do not sum to 1".
 */
export const normaliseWeights = (
  weights: Record<string, number>,
): Record<string, number> | null => {
  const entries = Object.entries(weights ?? {}).filter(
    ([level]) => typeof level === 'string' && level.length > 0,
  );
  if (entries.length === 0) return null;
  if (entries.some(([, w]) => !Number.isFinite(w) || w < 0)) return null;

  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  if (total <= 0) return null;

  return Object.fromEntries(entries.map(([level, w]) => [level, w / total]));
};

/** How many grid cells a draft produces: the product of the level counts. */
export const cellCount = (
  draft: Record<DemoDimension, DimensionDraft>,
): number =>
  DEMO_DIMENSIONS.reduce(
    (n, d) => n * Math.max(Object.keys(draft?.[d] ?? {}).length, 1),
    1,
  );

/**
 * Not a discriminated union: this repo compiles with `strict: false`, where
 * TypeScript will not narrow on a boolean literal, so `ok: true | false` would
 * force a cast at every call site. `problems.length` is the gate.
 */
export type DemographicResult = {
  /** null whenever `problems` is non-empty */
  demographic: Demographic | null;
  problems: string[];
};

/**
 * Turn the picker's draft into the stored payload, or say why it cannot.
 *
 * `trainedLevels` is the job's own `demo_levels` when it has them, and the
 * fixed vocabulary otherwise — `effectiveLevels` decides which.
 *
 * Checking here is not defence in depth. The translation path validates
 * weights and never levels, so an unseen level reaches `compute_eta_row`,
 * matches no coefficient, and renders as the reference level: a plausible
 * number belonging to a different cohort, with no error. R's
 * `.assert_trained_levels` repeats this check before rendering, and warns when
 * it is falling back to the vocabulary rather than a job's own levels.
 */
export const buildDemographic = (
  draft: Record<DemoDimension, DimensionDraft>,
  trainedLevels: Partial<Record<DemoDimension, string[]>> | null,
): DemographicResult => {
  const problems: string[] = [];
  const demographic = {} as Demographic;
  const known = effectiveLevels(trainedLevels);

  DEMO_DIMENSIONS.forEach((d) => {
    const weights = draft?.[d];
    const label = DIMENSION_LABEL[d];

    const normalised = normaliseWeights(weights);
    if (!normalised) {
      problems.push(
        Object.keys(weights ?? {}).length === 0
          ? `${label}: choose at least one level.`
          : `${label}: weights must include one above zero.`,
      );
      return;
    }

    const unseen = Object.keys(normalised).filter((l) => !known[d].includes(l));
    if (unseen.length) {
      problems.push(
        `${label}: the model never saw ${unseen.map(levelLabel).join(', ')} — it would be rendered as the reference level rather than failing.`,
      );
      return;
    }

    // The inputs are percentages, so they have to read as percentages. Left
    // unchecked, 70 and 40 would still normalise — to 63.6% and 36.4% — and
    // the stored population would be one nobody typed.
    const total = percentTotal(weights);
    if (Math.abs(total - PERCENT_TOTAL) > PERCENT_TOLERANCE) {
      problems.push(
        `${label}: percentages have to add up to 100% — they currently add up to ${Number(total.toFixed(2))}%.`,
      );
      return;
    }

    demographic[d] = normalised;
  });

  return {
    demographic: problems.length ? null : demographic,
    problems,
  };
};

/**
 * One-line summary of a stored demographic, for a list row.
 *
 * Takes the loose jsonb shape rather than `Demographic`: a row written by hand,
 * or by R, may be missing a dimension or carry `"typical"` — which the picker
 * no longer writes but `build_demo_grid` still accepts — and a list row is not
 * the place to throw over either. They are reported apart, because "the mean
 * cell" and "nothing was stored" are not the same thing.
 */
export const describeDemographic = (
  demographic: Partial<Record<string, unknown>> | null,
): string =>
  DEMO_DIMENSIONS.map((d) => {
    const v = demographic?.[d];
    if (v === 'typical') return `${DIMENSION_LABEL[d]}: typical`;
    if (!v || typeof v !== 'object') return `${DIMENSION_LABEL[d]}: —`;
    const levels = Object.keys(v as Record<string, number>);
    if (!levels.length) return `${DIMENSION_LABEL[d]}: —`;
    return `${DIMENSION_LABEL[d]}: ${
      levels.length > 3
        ? `${levels.length} levels`
        : levels.map(levelLabel).join(', ')
    }`;
  }).join(' · ');

/** One cell of the demographic grid: a level per dimension, and its weight. */
export type GridCell = Record<DemoDimension, string> & { weight: number };

/**
 * The grid `build_demo_grid` will produce from a stored demographic.
 *
 * One row per combination, weighted by the product of its marginals — the same
 * `w_ctry * w_age * w_gen * w_smk` the R side computes, and the same reason the
 * weights sum to 1 across the whole grid when each dimension does.
 *
 * Rebuilt here rather than stored: `weight` is derived, and keeping a copy of
 * it would only be a way to disagree with `build_demo_grid` later. This is a
 * preview of what R will do, not a second definition of it.
 */
const dimensionCells = (value: unknown): [string, number][] => {
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, number>);
  }
  // build_demo_grid's other branch: one cell carrying the whole weight.
  return [[typeof value === 'string' ? value : 'typical', 1]];
};

export const demoGrid = (demographic: Demographic | null): GridCell[] => {
  if (!demographic) return [];

  return DEMO_DIMENSIONS.reduce<GridCell[]>(
    (rows, d) =>
      rows.flatMap((row) =>
        // A stored row may carry the bare string "typical", which R reads as
        // one synthetic cell of weight 1. Object.entries would spell it out
        // character by character instead, giving seven levels named "0".."6"
        // and a weight of NaN.
        dimensionCells(demographic[d]).map(([level, w]) => ({
          ...row,
          [d]: level,
          weight: row.weight * w,
        })),
      ),
    [{ weight: 1 } as GridCell],
  );
};

/** Selected levels in the vocabulary's own order, not in click order. */
export const inLevelOrder = (
  dimension: DemoDimension,
  selected: string[],
): string[] =>
  [...selected].sort(
    (a, b) =>
      DEMO_LEVELS[dimension].indexOf(a) - DEMO_LEVELS[dimension].indexOf(b),
  );

/** The numeric bounds of an age band label, or null when it has none. */
const ageBounds = (label: string): [number, number | null] | null => {
  const openEnded = /^(\d+)\+$/.exec(label);
  if (openEnded) return [Number(openEnded[1]), null];
  const range = /^(\d+)-(\d+)$/.exec(label);
  if (range) return [Number(range[1]), Number(range[2])];
  return null;
};

/**
 * The selected age bands as a span, when they actually form one.
 *
 * Outer bounds only describe a selection if nothing inside it was skipped:
 * 18-20 through 35-44 is 18-44, but 6-12 with 55+ is not "6+" — that name
 * claims a population four decades wider than the one selected, and the name
 * is the only human-readable identifier a translation gets. So contiguity is
 * checked against the vocabulary rather than assumed, and a gapped selection
 * lists its bands instead.
 *
 * A band with no number in it — "Unknown" — cannot join a span either way, so
 * it is always listed alongside.
 */
const ageSpan = (levels: string[]): string => {
  const ordered = inLevelOrder('age_group', levels);
  const bounds = ordered.map(ageBounds);
  const numeric = ordered.filter((_l, i) => bounds[i]);
  const others = ordered.filter((_l, i) => !bounds[i]);

  if (!numeric.length) return others.join('/');

  const indices = numeric.map((l) => DEMO_LEVELS.age_group.indexOf(l));
  const contiguous = indices.every(
    (v, i) => i === 0 || v === indices[i - 1] + 1,
  );
  if (!contiguous) return [...numeric, ...others].join('/');

  const spans = numeric.map((l) => ageBounds(l));
  const low = Math.min(...spans.map(([l]) => l));
  const openEnded = spans.some(([, h]) => h === null);
  const span = openEnded
    ? `${low}+`
    : `${low}-${Math.max(...spans.map(([, h]) => h))}`;

  return [span, ...others].join('/');
};

/**
 * A name describing the audience the draft defines.
 *
 * Country, age span, gender — the three a person reads a translation by.
 * Smoking is left out deliberately: it is usually both levels, and a name that
 * always ends "No/Yes" stops carrying information.
 *
 * Derived from the draft rather than the saved payload so it tracks the picker
 * while weights are still being edited.
 */
export const suggestName = (
  draft: Record<DemoDimension, DimensionDraft>,
): string => {
  const selected = (d: DemoDimension) =>
    inLevelOrder(d, Object.keys(draft?.[d] ?? {}));

  const country = selected('country').map(levelLabel);
  const age = ageSpan(selected('age_group'));
  const gender = selected('gender').join('/');

  const tail = [age, gender].filter(Boolean).join(' ');
  if (!country.length) return tail;
  return tail ? `${country.join('/')}: ${tail}` : country.join('/');
};

/**
 * Whole numbers summing to exactly `total`, from numbers that do not.
 *
 * Largest remainder: floor everything, then hand the leftover points to the
 * levels that lost the most in the rounding. Rounding each independently would
 * miss by a point or two, and a distribution that adds up to 99 is not one.
 */
const roundToTotal = (values: number[], total: number): number[] => {
  if (!values.length) return [];

  const floors = values.map((v) => Math.floor(v));
  const out = [...floors];
  let short = total - floors.reduce((a, b) => a + b, 0);

  const byRemainder = values
    .map((v, i) => ({ i, r: v - floors[i] }))
    .sort((a, b) => b.r - a.r);

  // Hand out points where the most was lost; claw back where the least was.
  for (let k = 0; short > 0 && k < byRemainder.length; k += 1, short -= 1) {
    out[byRemainder[k].i] += 1;
  }
  for (let k = byRemainder.length - 1; short < 0 && k >= 0; k -= 1) {
    if (out[byRemainder[k].i] > 0) {
      out[byRemainder[k].i] -= 1;
      short += 1;
    }
  }

  return out;
};

/**
 * Move the boundary that sits after `index` to `cumulative` percent.
 *
 * A drag is a transfer between two neighbours, never a rescale: whatever one
 * segment gains, the one beside it loses. That is what keeps the total at 100
 * without anything having to re-check it afterwards — the invariant is in the
 * operation rather than in a validation step after the fact.
 */
export const moveBoundary = (
  weights: DimensionDraft,
  levels: string[],
  index: number,
  cumulative: number,
): DimensionDraft => {
  if (index < 0 || index >= levels.length - 1) return weights;
  // A pointer reading taken before the bar has been laid out is NaN, and NaN
  // would propagate through both neighbours and out into the stored payload.
  if (!Number.isFinite(cumulative)) return weights;

  const values = levels.map((l) => weights[l] ?? 0);
  const before = values.slice(0, index).reduce((a, b) => a + b, 0);
  const pair = values[index] + values[index + 1];

  // The boundary cannot pass its neighbours: the pair's own span is its range.
  const next = Math.round(
    Math.max(before, Math.min(before + pair, cumulative)),
  );

  const out = [...values];
  out[index] = next - before;
  out[index + 1] = pair - out[index];

  return Object.fromEntries(levels.map((l, i) => [l, out[i]]));
};

/**
 * Set one level's percentage, taking the difference from the rest.
 *
 * The others move proportionally, so the shape of the distribution the user
 * already built survives an edit to one part of it. When they are all at zero
 * there is no shape to preserve, so the remainder is split evenly.
 */
export const setPercentage = (
  weights: DimensionDraft,
  levels: string[],
  level: string,
  value: number,
): DimensionDraft => {
  if (!levels.includes(level)) return weights;

  if (!Number.isFinite(value)) return weights;
  const target = Math.round(Math.max(0, Math.min(PERCENT_TOTAL, value)));
  const others = levels.filter((l) => l !== level);
  if (!others.length) return { [level]: PERCENT_TOTAL };

  const remaining = PERCENT_TOTAL - target;
  const otherTotal = others.reduce((sum, l) => sum + (weights[l] ?? 0), 0);

  const raw = others.map((l) =>
    otherTotal > 0
      ? ((weights[l] ?? 0) / otherTotal) * remaining
      : remaining / others.length,
  );
  const rounded = roundToTotal(raw, remaining);

  return {
    ...Object.fromEntries(others.map((l, i) => [l, rounded[i]])),
    [level]: target,
  };
};
